import {
  setUser,
  setTag,
  setContext,
  captureException,
  captureMessage as sentryCaptureMessage,
  init,
  httpClientIntegration,
  replayIntegration,
  browserTracingIntegration,
  withScope,
  type SeverityLevel,
} from "@sentry/react";

/**
 * Identify a user in Sentry with relevant debugging context
 * This function carefully filters out any PII while providing helpful context
 *
 * @param userId Optional user ID (will be used as Sentry's user ID)
 * @param traits Optional user traits (will be filtered for PII)
 */
export const identifyUser = (userId?: string, traits?: Record<string, any>) => {
  try {
    // Set user ID if available
    if (userId)
      setUser({
        id: userId,
      });

    // Add useful debugging context as tags (key-value pairs)
    if (traits) {
      // Safe UTM parameters - not PII
      if (traits.utm_source) setTag("utm.source", traits.utm_source);
      if (traits.utm_medium) setTag("utm.medium", traits.utm_medium);
      if (traits.utm_campaign)
        setTag("utm.campaign", traits.utm_campaign);
      if (traits.utm_content) setTag("utm.content", traits.utm_content);
      if (traits.utm_term) setTag("utm.term", traits.utm_term);

      // Ad information - not PII
      if (traits.match_type) setTag("ad.match_type", traits.match_type);
      if (traits.ad_set_id) setTag("ad.set_id", traits.ad_set_id);
      if (traits.ad_id) setTag("ad.id", traits.ad_id);
      if (traits.audience) setTag("ad.audience", traits.audience);

      // Add landing page ID if available
      if (traits.landing_page_id)
        setTag("landing_page_id", traits.landing_page_id);

      // Add device and session context (safe non-PII)
      const deviceAndSessionContext: Record<string, any> = {};

      // Form completion flags (boolean only - no values)
      if (traits.has_questionnaire_answers !== undefined) {
        deviceAndSessionContext.has_questionnaire_answers =
          traits.has_questionnaire_answers;
      }

      if (traits.form_complete !== undefined) {
        deviceAndSessionContext.form_complete = traits.form_complete;
      }

      // Add browser info
      deviceAndSessionContext.user_agent = navigator.userAgent;
      deviceAndSessionContext.language = navigator.language;

      // Add as context object
      setContext("session_info", deviceAndSessionContext);
    }

    // Add timestamp for when user was identified
    setContext("identification", {
      timestamp: new Date().toISOString(),
      has_user_id: !!userId,
    });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Initialize Sentry with configuration
 * Should be called early in the application lifecycle
 */
export const initSentry = () => {
  // Only initialize if explicitly enabled
  if (import.meta.env.VITE_ENABLE_SENTRY !== "true") {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn("Sentry DSN not configured. Sentry will not be initialized.");
    return;
  }

  init({
    dsn,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    debug: import.meta.env.DEV,
    // 1.0 = 100% of transactions
    tracesSampleRate: 1.0,
    beforeSend(event) {
      // Filter out non-business related errors
      const ignoredErrors = [
        "Cannot read properties of undefined (reading 'domInteractive')",
        "undefined is not an object (evaluating 'window.webkit.messageHandlers)",
        "undefined is not an object (evaluating 'this._perf.domInteractive')",
        "Failed to execute 'removeChild' on 'Node'",
        "Cannot read properties of null (reading 'shadowRoot')",
        "WKWebView API client did not respond to this postMessage",
        "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
        "UnknownError: Database deleted by request of the user",
      ];

      // Check if the error message contains any of the ignored patterns
      if (
        event.exception?.values?.some((exception) =>
          ignoredErrors.some((ignored) => exception.value?.includes(ignored))
        )
      ) {
        return null; // Returning null prevents the event from being sent to Sentry
      }
      return event;
    },
    integrations: [
      httpClientIntegration(),
      replayIntegration({
        // Mask text input to avoid capturing sensitive data
        maskAllText: true,
        blockAllMedia: true,
      }),
      browserTracingIntegration(),
    ],
    // Add sendDefaultPii option to capture headers and cookies
    sendDefaultPii: false,
    replaysOnErrorSampleRate: 1.0,
  });
};

/**
 * Capture a message with additional context
 *
 * @param message The message to capture
 * @param level The level of the message (info, warning, error)
 * @param context Additional context to include with the message
 */
export const captureMessage = (
  message: string,
  level: SeverityLevel = "info",
  context?: Record<string, any>,
) => {
  if (context) {
    withScope((scope) => {
      scope.setLevel(level);
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      sentryCaptureMessage(message);
    });
    return;
  }
  sentryCaptureMessage(message, level);
};
